import { Injectable } from '@angular/core';
import {
  Function as ProtoFunction,
  FunctionHeader,
  FunctionParameter,
  FunctionParameterParameterLocation,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import {
  CurlImportArrayItem,
  CurlImportObject,
  CurlImportResult,
} from '../../components/curl-import-dialog/curl-import-dialog.component';
import { applyVMeta, buildVMetaSuffix } from './ai-tool-curl-metadata';

@Injectable({
  providedIn: 'root',
})
export class AiToolCurlService {
  /**
   * Converts a CurlImportResult to a Partial<ProtoFunction>
   */
  curlCommandToFunction(
    curlImport: CurlImportResult,
    formId?: string,
    formDescription?: string,
  ): Partial<ProtoFunction> {
    const functionParameters: Partial<FunctionParameter>[] = [
      ...curlImport.bodyParams.map((param) =>
        this.convertCurlImportObjectToFunctionParameter(param, false, FunctionParameterParameterLocation.LOCATION_BODY),
      ),
      ...curlImport.queryParams.map(
        (param): Partial<FunctionParameter> => ({
          name: param.name,
          type: 'string',
          location: FunctionParameterParameterLocation.LOCATION_QUERY_PARAM,
        }),
      ),
    ];

    const func: Partial<ProtoFunction> = {
      id: formId || '',
      description: formDescription || '',
      url: curlImport.url,
      methodType: curlImport.methodType,
      functionParameters: functionParameters as FunctionParameter[],
      headers: curlImport.headers.map(
        (header): Partial<FunctionHeader> => ({ key: header.key, value: header.value }),
      ) as FunctionHeader[],
    };
    return applyVMeta(func, curlImport.metadata);
  }

  /**
   * Converts a CurlImportObject to a Partial<FunctionParameter>
   */
  convertCurlImportObjectToFunctionParameter(
    curlObject: CurlImportObject,
    isNested: boolean,
    location?: FunctionParameterParameterLocation,
  ): Partial<FunctionParameter> {
    // Casts: proto's Partial<> only wraps level-1 request fields; deeper nested fields stay non-Partial.
    return {
      name: curlObject.name,
      type: curlObject.type,
      ...(!isNested && location !== undefined ? { location } : {}),
      ...(curlObject.properties && curlObject.properties.length > 0
        ? {
            properties: curlObject.properties.map((prop) =>
              this.convertCurlImportObjectToFunctionParameter(prop, true, location),
            ) as FunctionParameter[],
          }
        : {}),
      ...(curlObject.item
        ? {
            items: this.convertCurlImportArrayItemToFunctionParameter(curlObject.item) as FunctionParameter,
          }
        : {}),
    };
  }

  /**
   * Converts a CurlImportArrayItem to a Partial<FunctionParameter>
   */
  convertCurlImportArrayItemToFunctionParameter(arrayItem: CurlImportArrayItem): Partial<FunctionParameter> {
    return {
      description: arrayItem.description || '',
      type: arrayItem.type || 'string',
      ...(arrayItem.properties && arrayItem.properties.length > 0
        ? {
            properties: arrayItem.properties.map((prop) =>
              this.convertCurlImportObjectToFunctionParameter(prop, true, arrayItem.location),
            ) as FunctionParameter[],
          }
        : {}),
      ...(arrayItem.item
        ? {
            items: this.convertCurlImportArrayItemToFunctionParameter(arrayItem.item) as FunctionParameter,
          }
        : {}),
    };
  }

  /**
   * Creates a curl command from the tool and copies it to the clipboard
   */
  functionToCurlCommand(formValue: Partial<ProtoFunction>): string {
    let curlCommand = `curl -X ${formValue.methodType} `;

    if (formValue.headers && formValue.headers.length > 0) {
      formValue.headers.forEach((header: Partial<FunctionHeader>) => {
        if (header.key && header.value) {
          curlCommand += `-H '${header.key}: ${header.value}' `;
        }
      });
    }

    const requestBodyArgs: Record<string, unknown> = {};
    const urlQueryArgs: Record<string, string> = {};

    const getDefaultValue = (param: Partial<FunctionParameter>): unknown => {
      switch (param.type) {
        case 'integer':
          return 0;
        case 'number':
          return 0;
        case 'boolean':
          return false;
        case 'array':
          if (param.items) {
            return [getDefaultValue(param.items)];
          }
          return [];
        case 'object':
          if (param.properties && Array.isArray(param.properties)) {
            const obj: Record<string, unknown> = {};
            param.properties.forEach((prop: Partial<FunctionParameter>) => {
              if (!prop.name) return;
              obj[prop.name] = getDefaultValue(prop);
            });
            return obj;
          }
          return {};
        case 'string':
        default:
          return '';
      }
    };

    if (formValue.functionParameters && formValue.functionParameters.length > 0) {
      formValue.functionParameters.forEach((param: Partial<FunctionParameter>) => {
        if (!param.name) return;
        if (param.location === FunctionParameterParameterLocation.LOCATION_QUERY_PARAM) {
          urlQueryArgs[param.name] = '';
        } else {
          requestBodyArgs[param.name] = getDefaultValue(param);
        }
      });
    }

    if (Object.keys(requestBodyArgs).length > 0) {
      curlCommand += `-d '${JSON.stringify(requestBodyArgs)}' `;
    }

    let url = formValue.url || '';
    if (Object.keys(urlQueryArgs).length > 0) {
      const searchParams = new URLSearchParams(urlQueryArgs).toString();
      url += `?${searchParams}`;
    }
    curlCommand += `'${url}'`;

    return curlCommand + buildVMetaSuffix(formValue);
  }
}
